<template>
  <div class="evaluation">
    <Card>
      <div class="session-content-block">
        <h4 class="session-content-block__title" style="color: var(--attendee-brand-color);">
          <c-button
            size="sm"
            theme="default"
            @click="goBack()"
          >
            <i class="fa-regular fa-arrow-left" style="color: var(--attendee-text-primary-color);"></i>
          </c-button>

          {{ title }}
        </h4>
      </div>

      <evaluation-table-layout
        v-if="isSingleSession"
        title="Licensure Info"
        description="Please select your state and primary professional discipline. It is also recommended that you include your state license code here, so that it can be included on your CE Certificate."
      >
        <!-- ALSO USED FOR 'download-certificate-form-modal.vue' -->
        <license-info-form ref="licenseInfoRef" @change="changeField" />
      </evaluation-table-layout>

      <evaluation-form
        type="session"
        ref="evaluationForm"
        @submit="send"
        :is-fetching="isFetching"
        :error-status="errorStatus"
        :error-message="errorMessage"
        :submit-error="submitError"
        :show-indicator="showIndicator"
        :attendee-evaluation="attendeeEvaluation"
        :evaluation-questions="evaluationQuestions"
      />
    </Card>
  </div>
</template>

<script>
import evaluationsAPI from "../../../api/evaluationsAPI";
import EvaluationForm from "../../../components/evaluations/evaluations-form";
import common from "../../../helpers/common";
import Card from "../../../components/ui/cards/card";
import LicenseInfoForm from "../../../components/agenda/license-info-form";
import EvaluationTableLayout from "../../../components/evaluations/evaluation-table-layout/index";
import {mapGetters} from 'vuex'
import Notify from "../../../components/ui/Notify";

export default {
  components: {
    EvaluationTableLayout,
    LicenseInfoForm,
    EvaluationForm,
    Card,
    Notify,
  },
  data() {
    return {
      attendeeId: this.$route.params.attendeeId,
      errorStatus: null,
      errorMessage: null,
      submitError: null,
      isFetching: false,
      showIndicator: false,
      title: '',
      evaluationQuestions: [],
      attendeeEvaluation: {},
      radio: null,
      textArea: '',
      options: [
        {
          name: 'Strongly Agree',
          value: '1',
        },
        {
          name: 'Agree',
          value: '2',
        },
        {
          name: 'Neutral',
          value: '3',
        },
        {
          name: 'Disagree',
          value: '4',
        },
        {
          name: 'Strongly Disagree',
          value: '5',
        }
      ],

      fields: {
        stateId: null,
        disciplineId: null,
        discipline: '',
        license: '',
        licenseId: null,
      },
    }
  },
  computed: {
    ...mapGetters('attendeeStore', ['attendee', 'isSingleSession']),
  },
  mounted() {
    common.setHeaderText('Session Evaluation');

    this.fetch();
  },
  methods: {
    goBack() {
      // Returning to the previous page can re-open an already-submitted evaluation
      // and hit a 422. Send the attendee to the sessions list instead.
      this.$router.push({ name: 'Full Schedule', params: { attendeeId: this.attendeeId } });
    },

    fetch() {
      this.isFetching = true;
      this.errorStatus = null;
      this.errorMessage = null;

      evaluationsAPI.fetch(this.attendeeId, this.$route.params.sessionId)
        .then((res) => {
          let counter = 0;

          res.data?.data?.items.forEach(item => {
            item.evaluationQuestions.forEach(eq => {
              eq.elementNumber = counter;
              counter++;
            });
          });

          this.evaluationQuestions = res.data?.data?.items;

          this.title = res.data?.data?.title;
          this.attendeeEvaluation = {
            title: res.data?.data?.title,
            attendeeName: res.data?.data?.attendee_name,
            creditHours: res.data?.data?.credit_hours,
          }
        })
        .catch((error) => {
          this.errorStatus = error?.response?.status;
          this.errorMessage = error?.response?.data?.flash || null;
        })
        .finally(() => {
          this.isFetching = false;
        });
    },

    send() {
      if (this.isSingleSession) {
        this.$refs.licenseInfoRef.$refs.form.validate().then(success => {
          if (!success) {
            this.scrollToFirstErrorField(this.$refs.licenseInfoRef);
            return;
          }

          const {stateId, disciplineId, discipline, license, licenseId} = this.fields;

          let licenseData = {
            state_id: stateId,
            discipline_id: disciplineId,
            license
          }

          if (discipline) {
            licenseData.discipline_other = discipline
          }

          this.save(licenseData);
        });
      } else {
        this.save();
      }
    },

    save(licenseData) {
      this.$refs.evaluationForm.$refs.form.validate().then(success => {
        if (!success) {
          this.scrollToFirstErrorField(this.$refs.evaluationForm);
          return;
        }

        this.showIndicator = true;
        this.submitError = null;

        let data = {};

        this.evaluationQuestions.forEach(evaluation => {
          for (let key in evaluation.evaluationQuestions) {
            if (evaluation.evaluationQuestions.hasOwnProperty(key)) {
              if (!data[evaluation.section]) {
                data[evaluation.section] = []
              }

              // if type checkbox then send array of string
              if (evaluation.evaluationQuestions[key].answer_layout === 'checkbox') {
                const checkedItems = evaluation.evaluationQuestions[key].value
                  .filter(item => item.checked)
                  .map(item => item.name);

                const question = {
                  id: evaluation.evaluationQuestions[key].id,
                  value: checkedItems
                };
                if (evaluation.evaluationQuestions[key].presenter_id) {
                  question.presenter_id = evaluation.evaluationQuestions[key].presenter_id;
                }
                data[evaluation.section].push(question);
              } else {
                const question = {
                  id: evaluation.evaluationQuestions[key].id,
                  value: evaluation.evaluationQuestions[key].value,
                };
                if (evaluation.evaluationQuestions[key].presenter_id) {
                  question.presenter_id = evaluation.evaluationQuestions[key].presenter_id;
                }

                data[evaluation.section].push(question);
              }
            }
          }
        });

        if (this.isSingleSession) {
          data = { ...data, ...licenseData };
        }

        evaluationsAPI.send(this.attendeeId, this.$route.params.sessionId, data)
          .then((res) => {
            this.$store.dispatch("agendaStore/updateFlatSessionById", {id: res.data?.data?.id, session: res.data?.data});

            this.$nextTick(() => {
              this.$refs.evaluationForm.$refs.form.reset();
            });

            if (this.isSingleSession) {
              this.$store.commit("agendaStore/updateHasFetchedData", false);
              this.$store.dispatch("agendaStore/fetchAgenda", {attendeeId: this.attendeeId, conferenceId: this.attendee?.conference_id});
              this.$router.replace({ name: "Single Event" });
            } else {
              this.$router.push({ name: 'Full Schedule', params: { attendeeId: this.attendeeId } });
            }
          })
          .catch((error) => {
            const errs = error?.response?.data?.errors;

            if (errs) {
              // Map backend keys -> provider ids without changing child component
              if (this.isSingleSession && (errs.license_id || errs.state_id || errs.discipline_id || errs.discipline || errs.license)) {
                this.mapAndSetObserverErrorsForLicenseInfo(this.$refs.licenseInfoRef?.$refs?.form, errs);
                this.scrollToFirstErrorField(this.$refs.licenseInfoRef);
              } else {
                this.mapAndSetObserverErrors(this.$refs.evaluationForm?.$refs?.form, errs);
                this.scrollToFirstErrorField(this.$refs.evaluationForm);
              }
              // this.applyErrorClasses(errs); //TODO: if need uncomment for make items red
            } else {
              // A backend flash message (e.g. a business rule rejection) shown under the form.
              this.submitError = error?.response?.data?.flash || null;
            }
          })
          .finally(() => {
            this.showIndicator = false;
          });
      })
    },

    applyErrorClasses(errors) {
      // Clear all .text-danger
      document.querySelectorAll('[data-evaluation-index]').forEach(el => {
        el.classList.remove('text-danger');
      });

      Object.keys(errors).forEach(key => {
        const match = key.match(/^evaluation\.(\d+)\.value$/);
        if (match) {
          const index = match[1]; // for example, "22"

          // find element "data-evaluation-index"
          const el = document.querySelector(`[data-evaluation-index="${index}"]`);

          if (el) {
            el.classList.add('text-danger');
          }
        }
      });
    },

    scrollToFirstErrorField(ref) {
      this.$nextTick(() => {
        const formEl = ref?.$el;
        if (!formEl) return;

        // Find first element with error
        const firstInvalidInput = formEl.querySelector(
          '.text-danger, .is-invalid, .has-error'
        );

        if (firstInvalidInput?.scrollIntoView) {
          firstInvalidInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    },

    changeField(fields){
      this.fields = fields;
    },

    // Map server keys like "evaluation.22.value" to provider ids dynamically
    mapAndSetObserverErrors(observer, serverErrors) {
      if (!observer || !serverErrors) return;

      // observer.refs contains child ValidationProviders
      const providersObj = observer.refs || {};
      const providers = Array.isArray(providersObj) ? providersObj : Object.values(providersObj);

      const idErrorMap = {};

      providers.forEach((prov) => {
        // Find the inner input/select/textarea that has the name attribute
        const input = prov?.$el?.querySelector('[name]');
        const fieldName = input?.getAttribute?.('name');
        if (!fieldName) return;

        const messages = serverErrors[fieldName];
        if (!messages) return;

        // VeeValidate accepts a string or an array of strings
        idErrorMap[prov.id] = Array.isArray(messages) ? messages : [messages];
      });

      if (Object.keys(idErrorMap).length) {
        observer.setErrors(idErrorMap);
      }
    },

    mapAndSetObserverErrorsForLicenseInfo(observer, serverErrors) {
      if (!observer || !serverErrors) return;

      const providersObj = observer.refs || {};
      const providers = Array.isArray(providersObj) ? providersObj : Object.values(providersObj);

      const idErrorMap = {};

      providers.forEach((prov) => {
        // Prefer the provider's VID (matches your backend keys), fall back to its internal id
        const serverKey = prov?.vid || prov?.id;
        if (!serverKey) return;

        const messages = serverErrors[serverKey];
        if (!messages) return;

        // setErrors expects keys to be provider.id
        idErrorMap[prov.id] = Array.isArray(messages) ? messages : [messages];
      });

      if (Object.keys(idErrorMap).length) {
        observer.setErrors(idErrorMap);
      }
    },
  }
}
</script>

<style scoped lang="scss">
.evaluation {
  margin-top: var(--attendee-gap-md);

  .card{
    padding: var(--attendee-gap-sm);
  }
}
</style>

